<template>
	<div class="product-picker-page" :class="product.filter ? 'with-filter' : ''">
		<div class="static-height">
			<h1 :class="product.filter ? 'small-spacing' : ''">{{ product.pageName || product.name }}</h1>
		</div>

		<!-- Mobile icon -->
		<div class="filter-icon not-desktop">
			<div v-if="product.filter" @click="filtersAreOpen = !filtersAreOpen">
				<span class="text-container">Filtr</span>
				<img v-if="filtersAreOpen" :src="$imageUrl('close.svg')" width="22" class="icon-close">
				<img v-else :src="$imageUrl('filtr.svg')" width="22">
			</div>
			<div v-else class="conf-type" v-html="$tr.title[$backendConfig.settings.sex].replace(' ', '<br>')"></div>
		</div>

		<div class="static-height filters-wrapper" v-if="product.filter" :class="filtersAreOpen ? 'opened' : ''">
			<FilterWrapper :properties="product.filter" @input="setFilter" @closeFiltersModal="filtersAreOpen = false"/>
			<div class="filter-white-bg"></div>
		</div>
		<div class="row">
			<!-- Product options & fields -->
			<div class="options-wrapper" :class="[
				product.sidebar === 'product-info' || product.sidebar.substr(0, 4) === 'both' ? 'col-lg-8' : '',
				product.sidebar === 'fabric' ? 'col-10' : '',
				product.sidebar === 'none' ? 'col-12' : ''
			]" ref="optionsWrapper">
				<!-- Main product options -->
				<div class="">
					<ProductSelect :products="filteredProducts" :product="product"
								   :productId="product.id"
								   @openSidebar="sidebarIsOpen = true"
								   :overlay="productOverlay"
								   :showFabric="product.productOverlay || product.sidebar === 'both-over'"
								   :classes="
									product.sidebar === 'fabric'
										? 'col-md-4 col-lg-3'
										: (
											product.sidebar === 'none'
											? 'col-md-4 col-lg-2'
											: 'col-md-6 col-lg-4 col-xl-3'
										)
					"/>


					<!-- Secondary product options -->
					<div v-if="secondProduct" class="row second-half">
						<div class="col-12">
							<div class="desktop-only">
								<h5 class="heading fabric-line-after">{{ $tr.product.secondary.title }}</h5>
							</div>
							<div class="not-desktop">
								<h5 class="heading mb-3">{{ $tr.product.secondary.titleMobile }}</h5>
							</div>
							<ProductSelect :products="secondProduct.products"
										   :product="product"
										   :productId="product.id + '-' + secondProduct.id"
										   @openSidebar="sidebarIsOpen = true"
										   :classes="
									product.sidebar === 'fabric'
										? 'col-md-4 col-lg-3'
										: (
											product.sidebar === 'none'
											? 'col-md-4 col-lg-2'
											: 'col-md-6 col-lg-4 col-xl-3'
										)
					        "/>
						</div>
					</div>

					<!-- Product fields -->
					<div v-if="product.moreFields" class="row second-half">
						<div class="col-12 d-block d-lg-none" v-if="selectedVariant">
							<h5 class="heading">{{ selectedVariant.name }}</h5>
							<div class="row">
								<div class="col-5 pr-0">
									<div class="selected-product">
										<img :src="selectedVariant.mainImage" :alt="product.name" :title="product.name">
									</div>
								</div>
								<div class="col-7 pl-0">
									<SelectedFabric :hideHeading="true">
										<!-- Monogram is selected -> show styled monogram text -->
										<template v-if="product.id !== 'none'">
                                        <span
											v-if="$db.products['monogram-font'] && $db.products['monogram-text'] && $db.products['monogram-font-style']"
											class="fabric-text"
											:style="$db.products['monogram-font-style'] + ';' + $db.products['monogram-color-style']"
										>
                                            {{ $db.products["monogram-text"] }}
                                        </span>
										</template>
									</SelectedFabric>
								</div>
							</div>
						</div>
						<div class="col-12">
							<div v-for="field in product.moreFields" :class="'col-' + field.id" :key="field.id">
								<template v-if="field.id === 'text'">
									<ProductField :forProduct="product.id" :field="field" :productId="product.id"
												  :required="applyMoreFieldsValidation"/>
									<ProductField :forProduct="product.id" :field="field" :productId="product.id + '-1'"
												  :required="true" v-if="$db.products[product.id + '-1']"/>
								</template>
								<ProductField :field="field" :productId="product.id"
											  :required="applyMoreFieldsValidation" v-else/>
							</div>
						</div>
					</div>
				</div>

				<BottomBar :current="productIndex" :total="totalSteps" @goToStep="$emit('goToStep', $event)">
					<slot name="progress"></slot>
				</BottomBar>
			</div>

			<!-- Sidebar with product details -->
			<div class="sidebar-wrapper" :class="[
				product.sidebar === 'product-info' || product.sidebar.substr(0, 4) === 'both' ? 'col-md-4' : '',
				product.sidebar === 'fabric' ? 'col-2' : '',
			]" v-if="product.sidebar !== 'none'">
				<ProductSidebar :type="product.sidebar"
								:product="secondProduct ? $db.products[product.id + '-' + secondProduct.id] : selectedVariant"
								:category="product"
								:open="sidebarIsOpen"
								:overlay="productOverlay"
								:product-id="product.id"
								:key="JSON.stringify(selectedVariant) + (secondProduct ? JSON.stringify($db.products[product.id + '-' + secondProduct.id]) : '')"
								@close="sidebarIsOpen = false"
				/>
			</div>
		</div>
	</div>
</template>

<script>
	import FilterWrapper from "./ProductPickerPage/FilterWrapper";
	import ProductSelect from "./ProductPickerPage/ProductSelect";
	import ProductSidebar from "./ProductPickerPage/ProductSidebar";
	import ProductField from "./ProductPickerPage/ProductField";
	import SelectedFabric from "./common/SelectedFabric";
	import BottomBar from "./ProductPickerPage/BottomBar";
	import {EventBus} from "../event-bus";

	export default {
		name: "ProductPickerPage",
		components: {
			FilterWrapper,
			ProductSelect,
			ProductSidebar,
			ProductField,
			SelectedFabric,
			BottomBar
		},
		props: [
			"product",
			"secondProduct",
			"productIndex",
			"totalSteps",
		],
		computed: {
			// Filter shown products based on selected radios and checkboxes
			filteredProducts() {
				// Filter is empty
				if (!Object.keys(this.filter).length)
					return this.product.products;

				// Filter indexes that have to match product values (and at least one of the rest has to match)
				let haveToCorrespond = ["color", "pattern", "grammage"];
				return this.product.products.filter(product => {
					let detailedMatched = false;
					let detailedFailed = false;

					// Check filtered properties
					for (let property in this.filter) {
						let productValue = product.properties[property];
						let filterValue = this.filter[property];

						// Product's property value is same as one filter value
						if (this.valueMatches(filterValue, productValue)) {
							// Detailed property matches
							if (!haveToCorrespond.includes(property))
								detailedMatched = true;
						} else {
							// Property has to match -> filter this product out
							if (haveToCorrespond.includes(property))
								return false;

							// Mark that one of detailed properties failed
							detailedFailed = true;
						}
					}

					return !detailedFailed || detailedMatched;
				});
			},

			// If true make "moreFields" required
			applyMoreFieldsValidation() {
				return this.selectedVariant && this.selectedVariant.id !== "none";
			},

			selectedVariant() {
				return this.$db.products[this.product.id];
			},

			// Image of other product category o be shown above current one
			productOverlay() {
				let overlay = this.product.productOverlay;

				if (!overlay) return null;

				let product = this.$db.products[overlay];

				if (product)
					return product.mainImage;

				return null;
			}
		},
		watch: {
			applyMoreFieldsValidation(value) {
				if (!value)
					EventBus.$emit("invalid-more-fields", false);
			}
		},
		data() {
			return {
				filter: {},
				sidebarIsOpen: false,
				filtersAreOpen: false
			};
		},
		methods: {
			setFilter(key, value = null) {
				// Scroll product options wrapper to top after filtering
				this.$refs.optionsWrapper.scrollTo(0, 0);
				window.scrollTo(0,0);	// for mobile devices

				// Reset filter
				if (key === null)
					return this.filter = {};

				if (value === null)
					this.$delete(this.filter, key);
				else
					this.$set(this.filter, key, value);
			},

			// Check if product attribute value matches filter value
			valueMatches(filterValue, productValue) {
				return (
					// Product's value is array that includes filter value
					(
						Array.isArray(productValue)
						&&
						productValue.includes(filterValue)
					)
					||
					// Product's value is same as filter value
					(
						!Array.isArray(productValue)
						&&
						productValue === filterValue
					)
				);
			}
		}
	}
</script>
